import startCase from 'lodash/startCase';
import {
  SEVERITY_LEVELS,
  INCIDENT_STATUS,
} from 'xm-constants/incidentManagement';
import mapValues from 'lodash/mapValues';
import { incidentTriggerBuiltInTypes } from '@xmatters/common/constants/scriptSteps';

export const IncidentTriggerTypes = {
  ...SEVERITY_LEVELS,
  ...INCIDENT_STATUS,
  NOTIFY_TO_ENGAGE: 'NOTIFY_TO_ENGAGE',
};

export const IncidentTriggerSortOrder = [
  IncidentTriggerTypes.NOTIFY_TO_ENGAGE,
  IncidentTriggerTypes.CRITICAL,
  IncidentTriggerTypes.HIGH,
  IncidentTriggerTypes.MEDIUM,
  IncidentTriggerTypes.LOW,
  IncidentTriggerTypes.MINIMAL,
  IncidentTriggerTypes.OPEN,
  IncidentTriggerTypes.IN_PROGRESS,
  IncidentTriggerTypes.MITIGATED,
  IncidentTriggerTypes.RESOLVED,
  IncidentTriggerTypes.REJECTED,
];

export const IncidentTriggerBuiltinTypeByTriggerType = {
  ...mapValues(
    SEVERITY_LEVELS,
    () => incidentTriggerBuiltInTypes.SEVERITY_CHANGE,
  ),
  ...mapValues(
    INCIDENT_STATUS,
    () => incidentTriggerBuiltInTypes.STATUS_CHANGE,
  ),
  [IncidentTriggerTypes.NOTIFY_TO_ENGAGE]:
    incidentTriggerBuiltInTypes.NOTIFY_TO_ENGAGE,
};

export const IncidentTriggerConditionName = {
  SEVERITY: 'severity',
  STATUS: 'status',
  NOTIFY_TO_ENGAGE: 'notifyToEngage',
};

export const IncidentTriggerConditionByType = {
  ...mapValues(SEVERITY_LEVELS, type => ({
    name: IncidentTriggerConditionName.SEVERITY,
    value: type,
  })),
  ...mapValues(INCIDENT_STATUS, type => ({
    name: IncidentTriggerConditionName.STATUS,
    value: type,
  })),
  [IncidentTriggerTypes.NOTIFY_TO_ENGAGE]: {
    name: IncidentTriggerConditionName.NOTIFY_TO_ENGAGE,
    value: IncidentTriggerTypes.NOTIFY_TO_ENGAGE,
  },
};

export const IncidentTriggeredBy = {
  INCIDENT_SEVERITY_CHANGE: 'INCIDENT_SEVERITY_CHANGE',
  INCIDENT_STATUS_CHANGE: 'INCIDENT_STATUS_CHANGE',
  INCIDENT_NOTIFY_TO_ENGAGE: 'INCIDENT_NOTIFY_TO_ENGAGE',
};

export const IncidentTriggerChangeByTriggerType = {
  ...mapValues(
    SEVERITY_LEVELS,
    () => IncidentTriggeredBy.INCIDENT_SEVERITY_CHANGE,
  ),
  ...mapValues(
    INCIDENT_STATUS,
    () => IncidentTriggeredBy.INCIDENT_STATUS_CHANGE,
  ),
  [IncidentTriggerTypes.NOTIFY_TO_ENGAGE]:
    IncidentTriggeredBy.INCIDENT_NOTIFY_TO_ENGAGE,
};

export const IncidentTriggerOutputDescriptionPrefix = mapValues(
  IncidentTriggerTypes,
  triggerType => {
    if (SEVERITY_LEVELS[triggerType]) {
      return 'SEVERITY_OUTPUT';
    }
    if (INCIDENT_STATUS[triggerType]) {
      return 'STATUS_OUTPUT';
    }
    return `INIT_INCIDENT_TRIGGER_${triggerType}_OUTPUT`;
  },
);

export const isIncidentPropertyChangeTrigger = triggerType =>
  !!IncidentTriggerTypes[triggerType];

export const isIncidentPropertyChangeIntegration = triggeredBy =>
  !!IncidentTriggeredBy[triggeredBy];

export const isNotifyToEngageTrigger = triggerType =>
  triggerType === IncidentTriggerTypes.NOTIFY_TO_ENGAGE;

export const getTriggerDisplayName = triggerType => {
  if (!isIncidentPropertyChangeTrigger(triggerType)) {
    return null;
  }

  const name = startCase(triggerType.toLowerCase());
  if (SEVERITY_LEVELS[triggerType]) {
    return `Severity to ${name}`;
  }
  if (INCIDENT_STATUS[triggerType]) {
    return `Status to ${name}`;
  }

  return name;
};

export const getIncidentTriggerStepDisplayName = builtInType => {
  switch (builtInType) {
    case incidentTriggerBuiltInTypes.SEVERITY_CHANGE:
      return 'Severity Change';
    case incidentTriggerBuiltInTypes.STATUS_CHANGE:
      return 'Status Change';
    case incidentTriggerBuiltInTypes.NOTIFY_TO_ENGAGE:
      return 'Notify to Engage';
    default:
      return undefined;
  }
};

export const INCIDENT_DETAILS_PREFIX = 'incidentDetails.';

export const notifyToEngageProperties = ['resolvers'];
